<script setup>
import { ref, computed, onMounted } from 'vue'
import { Dialog } from 'quasar'
import { useRoute } from 'vue-router'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useNotify } from '@/composables/notify'
import { sortByFields } from '@/composables/filter-sort'
import { useWarehouseMovementCancel } from '@/composables/warehouse-movement-cancel'
import { useAllocationConfirm } from '@/composables/allocation-confirm'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import EquipmentCheckOutIn from '@/components/shared/EquipmentCheckOutIn.vue'
import NoStockConfirmDialog from '@/components/shared/NoStockConfirmDialog.vue'
import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import BottomTabsView from '@/components/shared/BottomTabsView.vue'
import FilterSelect from '@/components/shared/FilterSelect.vue'
import WarehouseFilterSelect from '@/components/shared/WarehouseFilterSelect.vue'
import WarehouseMovementEdit from '@/components/shared/WarehouseMovementEdit.vue'
import WarehouseMovementListItem from '@/components/planner/WarehouseMovementListItem.vue'

const i18n = getI18n()
const route = useRoute()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { notifySuccess, notifyError, notifyInfo } = useNotify()

const { SCHEDULED, CONFIRMED, CANCELLED } = planningStore.warehouseMovementStatusEnum
const { UNLOAD, LOAD } = planningStore.warehouseMovementTypeEnum

const props = defineProps({
  // Preselects the project filter when arriving from a project-scoped link (e.g. the timeline).
  // Kept just as the initial value: the user can still change or clear it afterwards.
  project: {
    type: Number,
    default: null,
  },
  warehouse: {
    type: Number,
    default: null,
  },
})

const loading = ref(true)
const warehouses = ref([])
const selectedWarehouseId = ref(props.warehouse)
const selectedProjectId = ref(props.project)
const movements = ref([])
const activeTab = ref('unconfirmed')
const tabs = [
  { name: 'unconfirmed', label: i18n.t('common.warehouse.unconfirmed') },
  { name: 'confirmed', label: i18n.t('common.warehouse.confirmed') },
]
const typeFilter = ref(null)

const typeFilterOptions = computed(() => [
  { label: i18n.t('common.all'), value: null },
  { label: i18n.t('common.checkOut'), value: UNLOAD },
  { label: i18n.t('common.checkIn'), value: LOAD },
])
const editItem = ref(null)
const scanItem = ref(null)
const isContinuousScan = ref(false)
const continuousScanTotal = ref(0)
const continuousScanDone = ref(0)

const isScannable = (movement) => movement.movement_type === UNLOAD || movement.movement_type === LOAD
const serviceUuid = (serviceId) => planningStore.getServiceById(serviceId)?.uuid

const startContinuousScan = () => {
  const total = unconfirmedMovements.value.filter(isScannable).length
  if (!total) {
    notifyInfo(i18n.t('common.noConfirmedEquipment'))
    return
  }
  continuousScanTotal.value = total
  continuousScanDone.value = 0
  isContinuousScan.value = true
}

webSiteStore.fabActions = [
  {
    id: 'add',
    icon: 'mdi-plus',
    onClick: () => { editItem.value = {} },
  },
  {
    id: 'scan',
    icon: 'mdi-qrcode-scan',
    onClick: startContinuousScan,
  },
]


const selectedWarehouse = computed(() => warehouses.value.find(w => w.id === selectedWarehouseId.value) ?? null)

const fetchMovementsAsync = async () => {
  loading.value = true
  try {
    const params = { all: true, service__type: portalStore.serviceTypeEnum.EQUIPMENT }
    if (selectedWarehouseId.value !== null) params.warehouse = selectedWarehouseId.value
    if (selectedProjectId.value !== null) params.project = selectedProjectId.value
    const res = await planningStore.fetchWarehouseMovementsAsync(params)
    movements.value = (res.data ?? res).filter(m => m.status !== CANCELLED)
  } finally {
    loading.value = false
  }
}

const onLocationSelect = async (id) => {
  selectedWarehouseId.value = id
  await fetchMovementsAsync()
}

const onProjectSelect = async (id) => {
  selectedProjectId.value = id
  await fetchMovementsAsync()
}

onMounted(async () => {
  const fetches = [planningStore.fetchWarehousesAsync({ all: true })]
  if (planningStore.equipments.length === 0) fetches.push(planningStore.fetchEquipmentsAsync({ all: true }, true))
  if (planningStore.projects.length === 0) fetches.push(planningStore.fetchProjectsAsync({ all: true }, true))
  const [whRes] = await Promise.all(fetches)

  warehouses.value = (whRes.data ?? whRes).filter(w => !w.archived)
  // A project can span several locations: don't lock the view to the user's default warehouse
  // when arriving scoped to a project — leave it on "All" so every one of them shows.
  if (selectedWarehouseId.value === null && !props.project) {
    const defWh = webSiteStore.getDefaultWarehouse()
    selectedWarehouseId.value = defWh && warehouses.value.some(w => w.id === defWh) ? defWh : (warehouses.value[0]?.id ?? null)
  }
  await fetchMovementsAsync()
})

const filterSort = computed(() => webSiteStore.filterSortFields[route.name])

const movementServiceName = (movement) => planningStore.getServiceName(planningStore.getServiceById(movement.service))

const searchedMovements = computed(() => {
  const search = filterSort.value?.filterValue
  if (!search) return movements.value
  const q = search.toLowerCase()
  return movements.value.filter(m =>
    [movementServiceName(m), m.warehouse_name, m.destination_warehouse_name, m.project_name]
      .some(value => value?.toLowerCase().includes(q))
  )
})

const sortValue = (movement, field) => {
  if (field === 'date') return movement.scheduled_at ?? null
  if (field === 'warehouse') return movement.warehouse_name?.toLowerCase() || null
  return movementServiceName(movement)?.toLowerCase() || null
}

const sortedMovements = computed(() => sortByFields(searchedMovements.value, filterSort.value?.sortFields, sortValue))

const typeFilteredMovements = computed(() =>
  typeFilter.value === null
    ? sortedMovements.value
    : sortedMovements.value.filter(m => m.movement_type === typeFilter.value),
)

// Purely status-driven: confirming/restoring a movement is what moves it between tabs, not its
// scheduled date.
const unconfirmedMovements = computed(() => typeFilteredMovements.value.filter(m => m.status === SCHEDULED))
const confirmedMovements = computed(() => typeFilteredMovements.value.filter(m => m.status === CONFIRMED))

const showConfirm = ref(false)
const pendingConfirmMovement = ref(null)
const { noStockDialog, runConfirmAsync } = useAllocationConfirm()

const onConfirmRequest = (movement) => {
  pendingConfirmMovement.value = movement
  showConfirm.value = true
}

const onConfirmProceed = async () => {
  const movement = pendingConfirmMovement.value
  pendingConfirmMovement.value = null
  if (!movement) return
  await runConfirmAsync(
    (force) => planningStore.confirmWarehouseMovementAsync(movement.id, force, { skipErrorMessage: true }),
    async () => {
      notifySuccess(i18n.t('info.updatedSuccessfully'))
      await fetchMovementsAsync()
    },
  )
}

const onMovementCancelled = async () => {
  notifySuccess(i18n.t('info.updatedSuccessfully'))
  await fetchMovementsAsync()
}
const {
  showConfirm: showCancelConfirm,
  confirmMessage: cancelConfirmMessage,
  requestCancel: onCancelRequest,
  onCancelProceed,
  onCancelDismiss,
} = useWarehouseMovementCancel(onMovementCancelled)

const onRestoreRequest = async (movement) => {
  await planningStore.revertWarehouseMovementAsync(movement.id)
  notifySuccess(i18n.t('info.updatedSuccessfully'))
  await fetchMovementsAsync()
}

const onDeleteRequest = (movement) => {
  Dialog.create({
    message: i18n.t('common.confirmDelete'),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    try {
      await planningStore.deleteWarehouseMovementAsync(movement.id)
      notifySuccess(i18n.t('info.deletedSuccessfully'))
      await fetchMovementsAsync()
    } catch (error) {
      console.error(error)
      notifyError(i18n.t('error.deleteFailed'))
    }
  })
}

const onEditItemChanged = () => {
  editItem.value = null
  fetchMovementsAsync()
}

const onRowScan = (movement) => { scanItem.value = movement }

const notifyScanResult = (movement) => {
  notifySuccess(i18n.t(movement.movement_type === LOAD ? 'common.equipmentCheckedIn' : 'common.equipmentCheckedOut'))
}

const onSingleScanned = async (equipment) => {
  const movement = scanItem.value
  scanItem.value = null
  if (!equipment?.id || !movement) return
  await planningStore.confirmWarehouseMovementAsync(movement.id)
  notifyScanResult(movement)
  await fetchMovementsAsync()
}

const allowedContinuousScanUuids = computed(() => [
  ...new Set(unconfirmedMovements.value.filter(isScannable).map(m => serviceUuid(m.service)).filter(Boolean)),
])

const onContinuousScanNext = async (equipment) => {
  const movement = equipment?.id
    ? unconfirmedMovements.value.find(m => m.service === equipment.id && isScannable(m))
    : null
  if (!movement) return
  await planningStore.confirmWarehouseMovementAsync(movement.id)
  notifyScanResult(movement)
  continuousScanDone.value += 1
  await fetchMovementsAsync()
  if (continuousScanDone.value >= continuousScanTotal.value) isContinuousScan.value = false
}
</script>
<template>
  <bottom-tabs-view
    v-model="activeTab"
    :tabs="tabs"
  >
    <q-dialog
      maximized
      :model-value="editItem !== null"
    >
      <warehouse-movement-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :warehouse="selectedWarehouse"
        @update="onEditItemChanged"
        @cancel="onEditItemChanged"
        @delete="onEditItemChanged"
      />
    </q-dialog>
    <confirm-dialog
      v-model="showConfirm"
      :message="i18n.t('common.confirmMovementSchedule')"
      icon="mdi-alert"
      icon-color="warning"
      @confirm="onConfirmProceed"
    />
    <confirm-dialog
      v-model="showCancelConfirm"
      :message="cancelConfirmMessage"
      icon="mdi-alert"
      icon-color="warning"
      @confirm="onCancelProceed"
      @cancel="onCancelDismiss"
    />
    <no-stock-confirm-dialog v-model="noStockDialog" />

    <filter-sort-selector hide-archived />

    <div class="row items-center">
      <warehouse-filter-select
        class="q-pa-sm col"
        :model-value="selectedWarehouseId"
        :warehouses="warehouses"
        :allow-all="!!props.project"
        @update:model-value="onLocationSelect"
      />
      <filter-select
        class="q-pa-sm col"
        :model-value="selectedProjectId"
        :items="planningStore.projects"
        icon="mdi-briefcase-outline"
        allow-all
        searchable
        @update:model-value="onProjectSelect"
      />
    </div>

    <div class="row items-center q-mb-sm">
      <q-btn-toggle
        v-model="typeFilter"
        class="q-ma-sm"
        spread
        no-caps
        unelevated
        toggle-color="primary"
        style="height: 1.5rem; width: 100%;"
        :options="typeFilterOptions"
      />
    </div>
    <q-separator />

    <q-tab-panels
      v-model="activeTab"
      animated
      class="col scroll"
      style="min-height: 0;"
    >
      <q-tab-panel name="unconfirmed" class="q-pa-none">
        <q-list v-if="unconfirmedMovements.length">
          <warehouse-movement-list-item
            v-for="movement in unconfirmedMovements"
            :key="movement.id"
            :movement="movement"
            :scannable="isScannable(movement)"
            @scan="onRowScan"
            @confirm="onConfirmRequest"
            @edit="editItem = { ...$event }"
            @cancel="onCancelRequest"
            @delete="onDeleteRequest"
          />
        </q-list>
        <div
          v-else-if="!loading"
          class="text-center text-grey-6 q-pa-xl"
        >
          {{ i18n.t('common.noData') }}
        </div>
      </q-tab-panel>

      <q-tab-panel name="confirmed" class="q-pa-none">
        <q-list v-if="confirmedMovements.length">
          <warehouse-movement-list-item
            v-for="movement in confirmedMovements"
            :key="movement.id"
            :movement="movement"
            @restore="onRestoreRequest"
          />
        </q-list>
        <div
          v-else-if="!loading"
          class="text-center text-grey-6 q-pa-xl"
        >
          {{ i18n.t('common.noData') }}
        </div>
      </q-tab-panel>
    </q-tab-panels>


    <q-dialog
      :model-value="scanItem !== null"
      maximized
      @hide="scanItem = null"
    >
      <equipment-check-out-in
        v-if="scanItem !== null"
        :equipment-id="scanItem.service"
        :allowed-uuids="[serviceUuid(scanItem.service)].filter(Boolean)"
        @update="onSingleScanned"
        @cancel="scanItem = null"
      />
    </q-dialog>
    <q-dialog
      :model-value="isContinuousScan"
      maximized
      @hide="isContinuousScan = false"
    >
      <equipment-check-out-in
        v-if="isContinuousScan"
        continuous
        :allowed-uuids="allowedContinuousScanUuids"
        :scan-total="continuousScanTotal"
        :scan-done="continuousScanDone"
        @next="onContinuousScanNext"
        @cancel="isContinuousScan = false"
      />
    </q-dialog>
  </bottom-tabs-view>
</template>
